import {
  Box,
  Button,
  Divider,
  FormLabel,
  IconButton,
  Input,
  InputAdornment,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { OnboardingServiceSignupAuthServiceEnum } from "@incendium/api";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import useOAuthPopup from "Hooks/useOAuthPopup";
import SocialSignupButton from "features/signup/woocommerce/SocialSignupButton";
import {
  loginWithSignupAuthResponse,
  socialAuthUrl,
  socialLogin,
} from "features/signup/services/signupService";
import { useCallback, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { login } from "Actions";
import { WashedForm } from "Components/UI/WashedForm";
import { useNotification } from "Hooks";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { motion } from "motion/react";

const socialButtonSx = {
  py: 1.25,
  fontWeight: 600,
  borderColor: alpha("#fff", 0.35),
  color: "common.white",
  backgroundColor: alpha("#fff", 0.08),
  "&:hover": {
    borderColor: "common.white",
    backgroundColor: alpha("#fff", 0.14),
  },
  "&.Mui-disabled": {
    borderColor: alpha("#fff", 0.2),
    color: alpha("#fff", 0.45),
  },
};

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [socialSubmitting, setSocialSubmitting] =
    useState<OnboardingServiceSignupAuthServiceEnum | null>(null);
  const pendingLoginRef = useRef<OnboardingServiceSignupAuthServiceEnum | null>(
    null,
  );
  const location = useLocation<{ from: string }>();
  const history = useHistory();

  const { showErrorNotification } = useNotification();
  const dispatch = useDispatch();

  const handleOAuthCode = useCallback(
    async (code: string) => {
      const service = pendingLoginRef.current;
      if (!service) {
        return;
      }

      try {
        const response = await socialLogin({
          service,
          code,
          redirectUrl: OAUTH_REDIRECT_URL,
        });
        await loginWithSignupAuthResponse(response, dispatch);
        const from = location.state?.from || "/";
        history.push(from);
      } catch (error: any) {
        let errorMessage = "Failed to log in. Please try again.";
        try {
          const errorData = await error.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // keep default message
        }
        showErrorNotification(errorMessage);
      } finally {
        pendingLoginRef.current = null;
        setSocialSubmitting(null);
      }
    },
    [dispatch, history, location.state?.from, showErrorNotification],
  );

  const { startOAuth } = useOAuthPopup({
    onCode: handleOAuthCode,
    onDenied: () => {
      pendingLoginRef.current = null;
      setSocialSubmitting(null);
    },
    cancelledMessage: "Login was cancelled",
  });

  const handleSocialLogin = useCallback(
    async (service: OnboardingServiceSignupAuthServiceEnum) => {
      setSocialSubmitting(service);
      pendingLoginRef.current = service;

      try {
        const opened = await startOAuth(async () => {
          const res = await socialAuthUrl({
            service,
            redirectUrl: OAUTH_REDIRECT_URL,
          });

          if (!res.authUrl) {
            throw new Error("Missing auth URL");
          }

          return res.authUrl;
        }, "login-auth");

        if (!opened) {
          pendingLoginRef.current = null;
          setSocialSubmitting(null);
        }
      } catch (error: any) {
        pendingLoginRef.current = null;
        setSocialSubmitting(null);

        let errorMessage = "Failed to start login. Please try again.";
        try {
          const errorData = await error.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // keep default message
        }
        showErrorNotification(errorMessage);
      }
    },
    [startOAuth, showErrorNotification],
  );

  const handleSignIn = async () => {
    dispatch(
      login({
        email,
        password,
        onSuccess: () => {
          const from = location.state?.from || "/";
          history.push(from);
        },
        onFail: () => {
          showErrorNotification("Internal Server Error");
        },
      }),
    );
  };

  const socialDisabled = socialSubmitting !== null;

  return (
    <>
      <WashedForm>
        <motion.div
          initial={{ x: -100, opacity: 0 }}
          animate={{
            x: 0,
            opacity: 1,
            transition: {
              delay: 0.2,
            },
          }}
          exit={{
            x: 300,
            opacity: 0,
            transition: {
              duration: 0.3,
            },
          }}
        >
          <Stack spacing={2}>
            <Stack spacing={1.5}>
              <SocialSignupButton
                service={OnboardingServiceSignupAuthServiceEnum.GOOGLE}
                label="Continue with Google"
                loading={
                  socialSubmitting ===
                  OnboardingServiceSignupAuthServiceEnum.GOOGLE
                }
                disabled={socialDisabled}
                onClick={() =>
                  handleSocialLogin(
                    OnboardingServiceSignupAuthServiceEnum.GOOGLE,
                  )
                }
                sx={socialButtonSx}
              />
              <SocialSignupButton
                service={OnboardingServiceSignupAuthServiceEnum.FACEBOOK}
                label="Continue with Facebook"
                loading={
                  socialSubmitting ===
                  OnboardingServiceSignupAuthServiceEnum.FACEBOOK
                }
                disabled={socialDisabled}
                onClick={() =>
                  handleSocialLogin(
                    OnboardingServiceSignupAuthServiceEnum.FACEBOOK,
                  )
                }
                sx={socialButtonSx}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Divider
                sx={{ flex: 1, borderColor: alpha("#fff", 0.25) }}
              />
              <Typography
                variant="body2"
                color="common.white"
                sx={{ whiteSpace: "nowrap", opacity: 0.85 }}
              >
                or log in with email
              </Typography>
              <Divider
                sx={{ flex: 1, borderColor: alpha("#fff", 0.25) }}
              />
            </Stack>

            <div>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                disabled={socialDisabled}
              />
            </div>
            <div>
              <FormLabel>Password</FormLabel>
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                fullWidth
                disabled={socialDisabled}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </div>
          </Stack>

          <Box mt={4} mb={2}>
            <Button
              onClick={handleSignIn}
              variant="contained"
              fullWidth
              color="primary"
              size="large"
              disabled={socialDisabled}
            >
              Log in
            </Button>
          </Box>

          <Box
            mt={1}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <Typography
              variant="body2"
              color="common.white"
              sx={{ opacity: 0.85 }}
            >
              By logging in you accept our{" "}
              <Typography
                component="a"
                href="https://www.incendium.ai/company/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                sx={{ textDecoration: "underline" }}
              >
                Privacy Policy
              </Typography>{" "}
              and{" "}
              <Typography
                component="a"
                href="https://www.incendium.ai/company/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                sx={{ textDecoration: "underline" }}
              >
                Terms & Conditions
              </Typography>
              .
            </Typography>
            <Typography color="white" fontWeight={500} sx={{ flexShrink: 0 }}>
              Forgot Password?
              <Typography component="span" ml={1}>
                <Link to="/password/forgot-password" color="primary">
                  Reset
                </Link>
              </Typography>
            </Typography>
          </Box>
        </motion.div>
      </WashedForm>
    </>
  );
};

export default LoginForm;
